import type { CurrentBiomassPerLotEntry } from '../../utils/currentBiomassPerLot'
import type { BiomassKPI } from '../Biomass'
import { resolveBiomassKpiLotIdentity } from './kpiLotIdentity'

// Findings #198: Biomasa's top cards took each lot as it was on its last
// weighed day, so a later count, the crew's mortality, a move or a harvest
// never reached "Total de Stock", and a lot never weighed (every egg lot) was
// left out. The cards now take each active lot's latest stock (last count less
// mortality since) with its last known weight. Biomass needs a weight: a lot
// without one counts in the stock, not in the biomass or the average weight.

type RosterLot = { id: string; lotKey?: string | null; stage?: string }

export type CurrentLotCards = {
  activeLots: BiomassKPI[]
  totalActiveStock: number
  totalActiveBiomass: number
  averageWeight: number
}

const emptyKpi = (lot: string, lotToken: string, date: string): BiomassKPI => ({
  lot,
  lotToken,
  stage: 'Active',
  date,
  totalBiomassKg: 0,
  fishStock: 0,
  rawStoredFishStock: null,
  averageWeightG: 0,
  mortality: 0,
  hasObservedMortalityInput: false,
  observedMortalitySource: null,
  observedMortalityRate: null,
  mortalityRate: null,
  survivalRate: null,
  expectedMortalityRate: null,
  projectedSurvivalRate: null,
  projectedStock: null,
  projectedBiomassKg: null,
  projectedStockCurrentTrend: null,
  projectedBiomassCurrentTrendKg: null,
  temperatureC: null,
  stockingDensity: null,
  densityLimitKgM3: null,
  densityTableName: null,
  feedConversionRatioInFlight: null,
  week: '',
  weekDisplay: ''
})

export const applyCurrentLotState = (args: {
  /** One KPI per active lot, from its last weighed day. */
  activeLots: readonly BiomassKPI[]
  /** The unit's lots. */
  roster: readonly RosterLot[]
  currentByLotId: ReadonlyMap<string, CurrentBiomassPerLotEntry>
}): CurrentLotCards => {
  const byToken = new Map(args.activeLots.map((kpi) => [kpi.lotToken, kpi] as const))
  const out = new Map<string, BiomassKPI>(byToken)

  for (const lot of args.roster) {
    if (lot.stage !== 'Active') continue
    const current = args.currentByLotId.get(String(lot.id))
    if (!current || current.sourceStockCount === null) continue
    const { token, label } = resolveBiomassKpiLotIdentity({ lot: lot.lotKey ?? '', lotId: lot.id }, args.roster)
    if (!token) continue
    const previous = byToken.get(token)
    const weight = current.sourceWeightG !== null && current.sourceWeightG > 0
      ? current.sourceWeightG
      : (previous && previous.averageWeightG > 0 ? previous.averageWeightG : 0)
    const stock = Math.max(0, current.sourceStockCount)
    out.set(token, {
      ...(previous ?? emptyKpi(label, token, current.asOfDate ?? '')),
      fishStock: stock,
      averageWeightG: weight,
      totalBiomassKg: weight > 0 ? (stock * weight) / 1000 : 0
    })
  }

  const activeLots = Array.from(out.values())
  let totalActiveStock = 0
  let totalActiveBiomass = 0
  let weighedStock = 0
  let weightedWeightSum = 0
  for (const kpi of activeLots) {
    totalActiveStock += kpi.fishStock
    totalActiveBiomass += kpi.totalBiomassKg
    if (kpi.averageWeightG > 0) {
      weighedStock += kpi.fishStock
      weightedWeightSum += kpi.averageWeightG * kpi.fishStock
    }
  }
  return {
    activeLots,
    totalActiveStock,
    totalActiveBiomass,
    averageWeight: weighedStock > 0 ? weightedWeightSum / weighedStock : 0
  }
}
